/**
 * Player ads controller (2026-10-30). Runs ONLY when the session carries an
 * `ads` plan (admin47 → Ads; default off). Never touches playback: every path
 * is wrapped so an ad problem can't stop the video.
 *
 * Trigger = a real click / tap / key press anywhere in the player document
 * (browsers only allow new windows during a user gesture). The first trusted
 * gesture is the "first play"; slot 1 with gap 0 fires on it.
 *
 *  - directlink: we window.open() the URL during the gesture — exact timing.
 *  - script:     the network tag is loaded once at start. A GATE wraps
 *                window.open and target=_blank link clicks: the tag can open a
 *                window only during a gesture when its slot is due. Otherwise
 *                its open is swallowed, so the network can't over-fire.
 */
export interface AdsPlanSlot { id: string; kind: "directlink" | "script"; url: string; code: string; gapMin: number; label: string }
export interface AdsPlan { slots: AdsPlanSlot[]; loop: boolean; loopGapMin: number; maxPerSession: number; crossEpisode: boolean; test: boolean }

const LAST_KEY = "ww-ads-last";
const MIN = 60_000;
type Log = (msg: string) => void;

export function startAds(plan: AdsPlan, log: Log = () => {}, onAdOpened: () => void = () => {}): { stop(): void; state(): Record<string, unknown> } {
  const slots = plan.slots.filter((s) => (s.kind === "directlink" ? /^https:\/\//.test(s.url) : !!s.code));
  const realOpen = window.open.bind(window);
  let idx = 0;
  let cycled = false;
  let shown = 0;
  let firstAt: number | null = null; // first trusted gesture
  let lastAt: number | null = null;
  let gateUntil = 0;
  let gateSlot: AdsPlanSlot | null = null;
  let gateConsumed = false;
  let scriptMisses = 0;
  let stopped = false;
  const history: Array<{ at: number; slot: string; how: string }> = [];

  let carried = 0;
  if (plan.crossEpisode) {
    try { carried = Number(localStorage.getItem(LAST_KEY) || 0) || 0; } catch { /* storage blocked */ }
  }

  const exhausted = () => stopped || !slots.length || (plan.maxPerSession > 0 && shown >= plan.maxPerSession) || (!plan.loop && idx >= slots.length);

  /** When the current slot becomes due (ms epoch), or null before the first play. */
  const dueAt = (): number | null => {
    if (firstAt === null || exhausted()) return null;
    const slot = slots[idx]!;
    let due: number;
    if (shown === 0 && !cycled) due = firstAt + slot.gapMin * MIN;
    else if (idx === 0 && cycled) due = (lastAt ?? firstAt) + plan.loopGapMin * MIN;
    else due = (lastAt ?? firstAt) + slot.gapMin * MIN;
    if (plan.crossEpisode && carried) due = Math.max(due, carried + plan.loopGapMin * MIN);
    return due;
  };

  // ── after the last ad of the session: clear network click-catchers ──────
  // 2026-11-04 (measured): Profiton leaves an invisible fixed layer
  // (z-index 2147483647) over the player after its popup, eating the
  // viewer's next pause/seek click. Once no ad is due any more, any such
  // layer a network added is removed. Before that it is left alone (the
  // network needs it to open the due ad).
  const ownNodes = new Set<Element>([...Array.from(document.body.children), ...Array.from(document.documentElement.children)]);
  const isCatcher = (el: Element) => {
    if (ownNodes.has(el) || (el as HTMLElement).dataset?.wwAd !== undefined || el.tagName === "SCRIPT") return false;
    const cs = getComputedStyle(el);
    return (cs.position === "fixed" || cs.position === "absolute") && Number(cs.zIndex) >= 100000;
  };
  let sweeper: MutationObserver | null = null;
  const sweep = () => {
    for (const el of [...Array.from(document.body.children), ...Array.from(document.documentElement.children)]) {
      if (isCatcher(el)) { el.remove(); log("removed a network overlay"); }
    }
  };
  const startSweeping = () => {
    if (sweeper) return;
    sweep();
    sweeper = new MutationObserver(() => sweep());
    sweeper.observe(document.body, { childList: true });
    sweeper.observe(document.documentElement, { childList: true });
  };

  /** End (exclusive) of the turn armed now: the due slot + its gap-0 fallbacks. */
  let turnEnd = 0;
  const advance = (how: string) => {
    const slot = slots[idx]!;
    const skipped = how === "script-skipped";
    if (!skipped) {
      shown++;
      lastAt = Date.now();
      history.push({ at: lastAt, slot: slot.label || slot.id, how });
      try { localStorage.setItem(LAST_KEY, String(lastAt)); } catch { /* */ }
      carried = 0;
      // Networks swallow the click they open on; let the player finish what
      // the viewer meant (after this event, so we never double-toggle).
      setTimeout(() => { try { onAdOpened(); } catch { /* never break playback */ } }, 0);
    }
    // One window per turn: the fallbacks of this turn are done too.
    idx = Math.max(idx + 1, turnEnd);
    turnEnd = 0;
    if (idx >= slots.length && plan.loop) { idx = 0; cycled = true; }
    scriptMisses = 0;
    // Networks remove their own layer right after the popup; give them a moment.
    if (exhausted()) setTimeout(startSweeping, 300);
    log(`${skipped ? "turn skipped (no network opened)" : "ad shown"}: ${slot.label || slot.id} (${how}); next due in ${dueAt() ? Math.round((dueAt()! - Date.now()) / 1000) + "s" : "never"}`);
  };

  // ── attribution: which slot's network is asking? ────────────────────────
  // Every script host a slot loads (its own <script src> plus whatever its
  // loader adds while it initialises) is recorded. A window request whose
  // call stack contains another slot's host is refused, so networks can't
  // take each other's turn. Unknown callers (inline-only tags) fall back to
  // "whoever asks while the gate is open".
  const slotHosts = new Map<string, Set<string>>();
  let injecting: string | null = null;
  const claim = (src: string) => {
    if (!injecting || !src) return;
    let host = "";
    try { host = new URL(src, location.href).host; } catch { return; }
    if (!host || host === location.host) return;
    for (const [sid, hs] of slotHosts) if (sid !== injecting && hs.has(host)) return;
    if (!slotHosts.has(injecting)) slotHosts.set(injecting, new Set());
    slotHosts.get(injecting)!.add(host);
  };
  const observer = new MutationObserver((muts) => {
    for (const m of muts) for (const n of Array.from(m.addedNodes)) if (n instanceof HTMLScriptElement && n.src) claim(n.src);
  });
  const attribute = (): string | null => {
    const stack = new Error().stack ?? "";
    for (const [sid, hs] of slotHosts) for (const h of hs) if (stack.includes(`//${h}/`)) return sid;
    return null;
  };
  /**
   * Fallback group (2026-11-04): the due slot plus the slots right after it
   * with gap 0. Any of them may take the due turn — the first network that
   * actually opens wins. So if network A declines (its own frequency cap),
   * network B fills the same click. Still exactly one window per turn.
   */
  const group = (): AdsPlanSlot[] => {
    const g: AdsPlanSlot[] = [];
    for (let i = idx; i < slots.length; i++) {
      if (i > idx && slots[i]!.gapMin !== 0) break;
      g.push(slots[i]!);
    }
    return g;
  };
  /** Gate decision for a third-party window request right now. */
  const mayOpen = (): boolean => {
    if (!gateOpen()) return false;
    const who = attribute();
    if (who && gateSlot && who !== gateSlot.id) {
      const g = group();
      const pos = g.findIndex((x) => x.id === who);
      if (pos <= 0) { log(`ad from another slot's network blocked (${who})`); return false; }
      idx += pos; // a fallback network took this turn
      gateSlot = g[pos]!;
    }
    return true;
  };

  // ── gate: third-party windows only while their slot is due ──────────────
  const gateOpen = () => Date.now() < gateUntil && !gateConsumed;
  const gatedOpenRef = window.open = function gatedOpen(...args: Parameters<typeof window.open>) {
    if (mayOpen()) {
      gateConsumed = true;
      const w = realOpen(...args);
      if (gateSlot) advance(w ? "script" : "script-blocked");
      return w;
    }
    log("ad window blocked (not due)");
    // A harmless stand-in, not null: some tags treat null as "popup blocked"
    // and fall back to redirecting THIS frame, which would kill the player.
    return inertWindow();
  } as typeof window.open;

  // 2026-11-04 (measured): a network opened its window through a blank
  // iframe's own window.open, bypassing the gate above, so the player could
  // neither limit nor see it. Every same-origin frame window gets the gate.
  const cwDesc = Object.getOwnPropertyDescriptor(HTMLIFrameElement.prototype, "contentWindow");
  const gateFrame = (w: Window | null) => {
    try { if (w && w !== window && w.open !== gatedOpenRef) (w as Window).open = gatedOpenRef; } catch { /* cross-origin frame: has no access to us anyway */ }
    return w;
  };
  if (cwDesc?.get) {
    Object.defineProperty(HTMLIFrameElement.prototype, "contentWindow", {
      configurable: true,
      enumerable: cwDesc.enumerable,
      get(this: HTMLIFrameElement) { return gateFrame(cwDesc.get!.call(this) as Window | null); },
    });
  }

  // target=_blank anchors clicked by scripts (synthetic clicks) — same gate.
  const onAnchor = (ev: MouseEvent) => {
    const a = (ev.target as Element | null)?.closest?.("a[target=_blank]") as HTMLAnchorElement | null;
    if (!a || a.dataset.wwOwn !== undefined || ev.isTrusted) return;
    if (mayOpen()) { gateConsumed = true; if (gateSlot) advance("script-link"); return; }
    ev.preventDefault();
    ev.stopImmediatePropagation();
    log("ad link blocked (not due)");
  };

  // ── mute network click handlers once no ad is due ───────────────────────
  // 2026-11-04 (measured): after its popup, Profiton's click handler kept
  // swallowing the viewer's pause/seek clicks for the rest of the play.
  // Pointer/click listeners registered by third-party code (a call stack
  // with no frame from our own origin) are wrapped; after the session's last
  // ad they no longer receive events. Our own listeners are never touched.
  const POINTER = new Set(["click", "mousedown", "mouseup", "pointerdown", "pointerup", "touchstart", "touchend", "auxclick", "contextmenu"]);
  const realAdd = EventTarget.prototype.addEventListener;
  const realRemove = EventTarget.prototype.removeEventListener;
  const wrapped = new WeakMap<object, EventListener>();
  const ownFrame = `${location.origin}/`;
  // Third-party = any http(s) frame in the call chain from another origin
  // (our own code never runs inside an ad network's call stack).
  const foreignCaller = () => {
    const st = new Error().stack ?? "";
    return st.split("\n").some((l) => /https?:\/\//.test(l) && !l.includes(ownFrame));
  };
  EventTarget.prototype.addEventListener = function (this: EventTarget, type: string, fn: EventListenerOrEventListenerObject | null, opts?: boolean | AddEventListenerOptions) {
    if (fn && POINTER.has(type) && foreignCaller()) {
      let w = wrapped.get(fn);
      if (!w) {
        w = function (this: unknown, ev: Event) {
          if (exhausted()) return;
          return typeof fn === "function" ? fn.call(this, ev) : fn.handleEvent(ev);
        };
        wrapped.set(fn, w);
      }
      return realAdd.call(this, type, w, opts);
    }
    return realAdd.call(this, type, fn, opts);
  };
  EventTarget.prototype.removeEventListener = function (this: EventTarget, type: string, fn: EventListenerOrEventListenerObject | null, opts?: boolean | EventListenerOptions) {
    realRemove.call(this, type, (fn && wrapped.get(fn)) || fn, opts);
  };

  // ── load network scripts once, in chain order (closed gate until due) ──
  // One at a time with a short settle window so each loader's extra scripts
  // are attributed to the right slot.
  observer.observe(document.documentElement, { childList: true, subtree: true });
  void (async () => {
    for (const s of slots) {
      if (s.kind !== "script" || stopped) continue;
      injecting = s.id;
      try { injectHtml(s.code, s.id, claim); } catch (e) { log(`slot ${s.id} script failed to load: ${String(e)}`); }
      await new Promise((r) => setTimeout(r, 1500));
    }
    injecting = null;
    setTimeout(() => observer.disconnect(), 8000);
    log(`network hosts: ${[...slotHosts].map(([k, v]) => `${k}=${[...v].join("|")}`).join(" ") || "none (inline tags)"}`);
  })();

  // ── the trigger ────────────────────────────────────────────────────────
  // Press start (pointerdown/mousedown/touchstart): arm a due SCRIPT slot so a
  // tag that opens on mousedown or click gets through. Click / Enter / Space:
  // open a due DIRECT LINK (click carries user activation on mouse AND touch).
  const dueSlot = (): AdsPlanSlot | null => {
    if (stopped) return null;
    const now = Date.now();
    if (firstAt === null) firstAt = now;
    const due = dueAt();
    return due !== null && now >= due ? slots[idx]! : null;
  };

  const armScript = (slot: AdsPlanSlot) => {
    if (gateSlot === slot && gateOpen()) return; // already armed for this press
    gateSlot = slot;
    gateConsumed = false;
    gateUntil = Date.now() + 2000;
    turnEnd = idx + group().length;
    setTimeout(() => {
      if (gateSlot !== slot) return;
      if (!gateConsumed) {
        scriptMisses++;
        log(`slot ${slot.label || slot.id}: network did not open (miss ${scriptMisses}/3)`);
        if (scriptMisses >= 3) advance("script-skipped");
      }
      gateSlot = null;
    }, 2100);
  };

  const onPress = (ev: Event) => {
    if (!ev.isTrusted) return;
    try {
      const slot = dueSlot();
      if (slot?.kind === "script") armScript(slot);
    } catch (e) { log(`ads error: ${String(e)}`); }
  };

  const onActivate = (ev: Event) => {
    if (!ev.isTrusted) return;
    if (ev instanceof KeyboardEvent && !["Enter", " ", "Spacebar", "k", "K"].includes(ev.key)) return;
    try {
      const slot = dueSlot();
      if (!slot) return;
      if (slot.kind === "script") {
        armScript(slot);
        // Fallback direct link in the same turn (2026-11-05): if the network
        // script did not open within a moment of this click (e.g. its own
        // frequency cap), open the direct link instead. The click's user
        // activation is still unused, so the browser allows it.
        const dl = group().find((x) => x.kind === "directlink");
        if (dl) {
          setTimeout(() => {
            if (stopped || gateSlot !== slot || gateConsumed) return;
            gateConsumed = true;
            const w = realOpen(dl.url, "_blank");
            if (w) { try { w.opener = null; } catch { /* */ } }
            idx = slots.indexOf(dl);
            advance(w ? "link-fallback" : "link-blocked");
          }, 450);
        }
        return;
      }
      const w = realOpen(slot.url, "_blank");
      if (w) {
        try { w.opener = null; } catch { /* cross-origin already */ }
        // The ad took this click: it must not also toggle the player (it would
        // pause an autoplaying video). onAdOpened then makes sure it plays.
        ev.preventDefault();
        ev.stopImmediatePropagation();
      }
      advance(w ? "link" : "link-blocked");
    } catch (e) { log(`ads error: ${String(e)}`); }
  };

  window.addEventListener("click", onAnchor, true);
  for (const t of ["pointerdown", "mousedown", "touchstart"]) window.addEventListener(t, onPress, true);
  window.addEventListener("click", onActivate, true);
  window.addEventListener("keydown", onActivate, true);
  log(`ads on: ${slots.length} slot(s)${plan.test ? " [TEST MODE]" : ""}`);

  return {
    stop() {
      stopped = true;
      window.open = realOpen;
      if (cwDesc) Object.defineProperty(HTMLIFrameElement.prototype, "contentWindow", cwDesc);
      sweeper?.disconnect();
      EventTarget.prototype.addEventListener = realAdd;
      EventTarget.prototype.removeEventListener = realRemove;
      window.removeEventListener("click", onAnchor, true);
      for (const t of ["pointerdown", "mousedown", "touchstart"]) window.removeEventListener(t, onPress, true);
      window.removeEventListener("click", onActivate, true);
      window.removeEventListener("keydown", onActivate, true);
    },
    state: () => ({ idx, shown, cycled, firstAt, lastAt, dueAt: dueAt(), history: [...history] }),
  };
}

/** An object that absorbs any window-like use (w.location = …, w.document.write, w.focus()). */
function inertWindow(): Window {
  const fn = () => proxy;
  const proxy: Window = new Proxy(fn as unknown as Window, {
    get(_t, key) {
      if (key === "closed") return false;
      if (key === Symbol.toPrimitive) return () => "";
      if (key === "then") return undefined; // never look like a Promise
      return proxy;
    },
    set: () => true,
    apply: () => proxy,
  });
  return proxy;
}

/** Insert raw HTML (network tag). <script> elements are re-created so they execute. */
function injectHtml(html: string, id: string, claim: (src: string) => void = () => {}): void {
  const host = document.createElement("div");
  host.hidden = true;
  host.dataset.wwAd = id;
  const tpl = document.createElement("template");
  tpl.innerHTML = html;
  // Scripts parsed via innerHTML never run: re-create each one (also nested ones), in order.
  const scripts = Array.from(tpl.content.querySelectorAll("script"));
  for (const node of scripts) {
    const sc = document.createElement("script");
    for (const at of Array.from(node.attributes)) sc.setAttribute(at.name, at.value);
    sc.text = node.text;
    sc.dataset.wwAd = id;
    if (node.src) claim(node.src);
    node.remove();
    document.body.appendChild(sc);
  }
  if (tpl.content.childNodes.length) {
    host.appendChild(tpl.content);
    document.body.appendChild(host);
  }
}
